// src/components/ScheduleForm.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Error403Page from "./Error_backend";
import api from "../api.js";
import "../style/css/AppointmentForm.css";

interface DailySchedule {
  start_time: string;
  end_time: string;
  start_break_time: string;
  end_break_time: string;
  active: boolean;
}

interface ScheduleFormData {
  [key: string]: DailySchedule;
  monday: DailySchedule;
  tuesday: DailySchedule;
  wednesday: DailySchedule;
  thursday: DailySchedule;
  friday: DailySchedule;
  saturday: DailySchedule;
  sunday: DailySchedule;
}

const ScheduleForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<ScheduleFormData>({
    monday: {
      start_time: "",
      end_time: "",
      start_break_time: "",
      end_break_time: "",
      active: true,
    },
    tuesday: {
      start_time: "",
      end_time: "",
      start_break_time: "",
      end_break_time: "",
      active: true,
    },
    wednesday: {
      start_time: "",
      end_time: "",
      start_break_time: "",
      end_break_time: "",
      active: true,
    },
    thursday: {
      start_time: "",
      end_time: "",
      start_break_time: "",
      end_break_time: "",
      active: true,
    },
    friday: {
      start_time: "",
      end_time: "",
      start_break_time: "",
      end_break_time: "",
      active: true,
    },
    saturday: {
      start_time: "",
      end_time: "",
      start_break_time: "",
      end_break_time: "",
      active: false,
    },
    sunday: {
      start_time: "",
      end_time: "",
      start_break_time: "",
      end_break_time: "",
      active: false,
    },
  });

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // API base URL
  const API_BASE = "offices/availability";

  // Day labels
  const days = [
    { key: "monday", label: "Lunes" },
    { key: "tuesday", label: "Martes" },
    { key: "wednesday", label: "Miércoles" },
    { key: "thursday", label: "Jueves" },
    { key: "friday", label: "Viernes" },
    { key: "saturday", label: "Sábado" },
    { key: "sunday", label: "Domingo" },
  ];

  // Validate form data
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    days.forEach((day) => {
      const dayData = formData[day.key];

      if (dayData.active) {
        // Required fields validation for active days
        if (!dayData.start_time) {
          errors[`${day.key}_start_time`] =
            `La hora de inicio del ${day.label} es requerida`;
        }

        if (!dayData.end_time) {
          errors[`${day.key}_end_time`] =
            `La hora de fin del ${day.label} es requerida`;
        }

        // Time validation - end time must be after start time
        if (dayData.start_time && dayData.end_time) {
          const startTime = new Date(`2000-01-01T${dayData.start_time}`);
          const endTime = new Date(`2000-01-01T${dayData.end_time}`);

          if (endTime <= startTime) {
            errors[`${day.key}_end_time`] =
              `La hora de fin debe ser posterior a la hora de inicio del ${day.label}`;
          }
        }

        // Break time validation if provided
        if (dayData.start_break_time && dayData.end_break_time) {
          const breakStart = new Date(`2000-01-01T${dayData.start_break_time}`);
          const breakEnd = new Date(`2000-01-01T${dayData.end_break_time}`);

          if (breakEnd <= breakStart) {
            errors[`${day.key}_end_break_time`] =
              `La hora de fin del descanso debe ser posterior a la hora de inicio del ${day.label}`;
          }

          // Break must be within working hours
          if (dayData.start_time && dayData.end_time) {
            const startTime = new Date(`2000-01-01T${dayData.start_time}`);
            const endTime = new Date(`2000-01-01T${dayData.end_time}`);

            if (breakStart < startTime || breakEnd > endTime) {
              errors[`${day.key}_start_break_time`] =
                `El descanso debe estar dentro del horario laboral del ${day.label}`;
            }
          }
        }
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Fetch schedule data if in edit mode
  useEffect(() => {
    const fetchSchedule = async () => {
      // if (!id) return;

      try {
        setLoading(true);
        setError(null);
        let url = "";
        if (id) {
          url = "offices/" + id + "/availability/";
        } else {
          url = "offices/availability/";
        }
        const response = await api.get(url);
        if (!response) {
          throw new Error("Failed to fetch schedule");
        }

        const scheduleData = await response.data;
        setFormData(scheduleData);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchSchedule();
  }, [id]);

  const handleDayChange = (
    dayKey: string,
    field: string,
    value: string | boolean,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        [field]: value,
      },
    }));

    // Clear field errors when user starts typing
    const errorKey = `${dayKey}_${field}`;
    if (formErrors[errorKey]) {
      setFormErrors((prev) => ({
        ...prev,
        [errorKey]: "",
      }));
    }
  };

  const handleCopyToAll = (sourceDayKey: string) => {
    const sourceData = formData[sourceDayKey];
    const newFormData = { ...formData };

    days.forEach((day) => {
      if (day.key !== sourceDayKey) {
        newFormData[day.key] = { ...sourceData };
      }
    });

    setFormData(newFormData);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate form
    const isValid = validateForm();
    if (!isValid) {
      // Scroll to first error for better UX
      const firstErrorKey = Object.keys(formErrors)[0];
      if (firstErrorKey) {
        const element = document.getElementById(firstErrorKey);
        element?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let response;
      if (isEditMode) {
        const url = "/offices/" + id + "/availability/";
        response = await api.put(url, formData);
      } else {
        response = await api.post(`${API_BASE}/`, formData);
      }

      if (!response) {
        throw new Error(
          `Failed to ${isEditMode ? "update" : "create"} schedule`,
        );
      }

      navigate("/offices"); // Adjust to your schedules list route
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = async () => {
    if (!isEditMode || !id) {
      return;
    }

    if (
      !window.confirm("¿Estás seguro de que quieres eliminar este horario?")
    ) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      const response = await api.delete(`${API_BASE}/${id}/`);

      if (!response) {
        throw new Error("Failed to delete schedule");
      }

      navigate("/offices");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setDeleting(false);
    }
  };

  const handleBackClick = () => {
    navigate("/offices"); // Adjust to your schedules list route
  };

  // Show loading state
  if (loading) {
    return (
      <div className="form-entire-container-form-appointment">
        <div className="form-container-form-appointment">
          <div className="loading-message-form-appointment">
            <i className="fas fa-spinner fa-spin"></i> Cargando horario...
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && isEditMode) {
    return (
      <Error403Page
        code={500}
        title="Recurso no disponible."
        message="Hemos tenido problemas para acceder al recurso."
        homeHref="/"
        loginHref="/login"
        supportHref="/support"
        onHomeClick={(e) => {
          e.preventDefault();
          navigate("/");
        }}
        onLoginClick={(e) => {
          e.preventDefault();
          navigate("/login");
        }}
      />
    );
  }

  const titleText = isEditMode ? "Editar Horario" : "Crear Nuevo Horario";

  return (
    <div className="form-entire-container-form-appointment">
      <div className="form-container-form-appointment">
        <div className="header-actions-form-appointment">
          <button
            className="back-link-form-appointment"
            onClick={handleBackClick}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <i className="fas fa-arrow-left"></i> Volver a Horarios
          </button>
          <h2 style={{ margin: 0, fontSize: "1.5rem" }}>
            <i
              className="fas fa-calendar-alt"
              style={{ color: "var(--color-accent)" }}
            ></i>{" "}
            {titleText}
          </h2>
          <span></span>
        </div>

        <div className="form-card-form-appointment">
          <h3 style={{ marginBottom: "1.5rem", textAlign: "center" }}>
            {isEditMode ? "Editando horario semanal" : "Nuevo horario semanal"}
          </h3>

          {error && (
            <div className="message-box-form-appointment message-error-form-appointment">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {days.map((day) => {
              const dayData = formData[day.key];
              const isActive = dayData.active;

              return (
                <div key={day.key} className="form-group-form-appointment">
                  <div
                    style={{
                      padding: "var(--space-md)",
                      background: isActive
                        ? "var(--neutral-100)"
                        : "rgba(0,0,0,0.02)",
                      borderRadius: "var(--border-radius)",
                      border: `2px solid ${
                        isActive ? "var(--color-accent)" : "var(--neutral-300)"
                      }`,
                      opacity: isActive ? 1 : 0.7,
                    }}
                  >
                    {/* Day Header with Toggle */}
                    <div className="toggle-switch-container-form-appointment">
                      <label
                        className="toggle-switch-label-form-appointment"
                        style={{
                          marginBottom: 0,
                          fontSize: "1.1rem",
                          fontWeight: 600,
                        }}
                      >
                        {day.label}
                      </label>
                      <label className="toggle-switch-form-appointment">
                        <input
                          type="checkbox"
                          checked={isActive}
                          onChange={(e) =>
                            handleDayChange(day.key, "active", e.target.checked)
                          }
                          disabled={saving || deleting}
                        />
                        <span className="slider-form-appointment"></span>
                      </label>
                    </div>

                    {isActive && (
                      <div style={{ marginTop: "var(--space-md)" }}>
                        {/* Working Hours */}
                        <div className="grid-half-form-appointment">
                          <div className="form-group-form-appointment">
                            <label htmlFor={`${day.key}_start_time`}>
                              Hora de Inicio *
                            </label>
                            <input
                              type="time"
                              id={`${day.key}_start_time`}
                              value={dayData.start_time}
                              onChange={(e) =>
                                handleDayChange(
                                  day.key,
                                  "start_time",
                                  e.target.value,
                                )
                              }
                              required
                              disabled={saving || deleting}
                              className={
                                formErrors[`${day.key}_start_time`]
                                  ? "input-error"
                                  : ""
                              }
                            />
                            {formErrors[`${day.key}_start_time`] && (
                              <span className="field-error">
                                {formErrors[`${day.key}_start_time`]}
                              </span>
                            )}
                          </div>

                          <div className="form-group-form-appointment">
                            <label htmlFor={`${day.key}_end_time`}>
                              Hora de Fin *
                            </label>
                            <input
                              type="time"
                              id={`${day.key}_end_time`}
                              value={dayData.end_time}
                              onChange={(e) =>
                                handleDayChange(
                                  day.key,
                                  "end_time",
                                  e.target.value,
                                )
                              }
                              required
                              disabled={saving || deleting}
                              className={
                                formErrors[`${day.key}_end_time`]
                                  ? "input-error"
                                  : ""
                              }
                            />
                            {formErrors[`${day.key}_end_time`] && (
                              <span className="field-error">
                                {formErrors[`${day.key}_end_time`]}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Break Hours */}
                        <div className="grid-half-form-appointment">
                          <div className="form-group-form-appointment">
                            <label htmlFor={`${day.key}_start_break_time`}>
                              Inicio Descanso
                            </label>
                            <input
                              type="time"
                              id={`${day.key}_start_break_time`}
                              value={dayData.start_break_time}
                              onChange={(e) =>
                                handleDayChange(
                                  day.key,
                                  "start_break_time",
                                  e.target.value,
                                )
                              }
                              disabled={saving || deleting}
                              className={
                                formErrors[`${day.key}_start_break_time`]
                                  ? "input-error"
                                  : ""
                              }
                            />
                            {formErrors[`${day.key}_start_break_time`] && (
                              <span className="field-error">
                                {formErrors[`${day.key}_start_break_time`]}
                              </span>
                            )}
                          </div>

                          <div className="form-group-form-appointment">
                            <label htmlFor={`${day.key}_end_break_time`}>
                              Fin Descanso
                            </label>
                            <input
                              type="time"
                              id={`${day.key}_end_break_time`}
                              value={dayData.end_break_time}
                              onChange={(e) =>
                                handleDayChange(
                                  day.key,
                                  "end_break_time",
                                  e.target.value,
                                )
                              }
                              disabled={saving || deleting}
                              className={
                                formErrors[`${day.key}_end_break_time`]
                                  ? "input-error"
                                  : ""
                              }
                            />
                            {formErrors[`${day.key}_end_break_time`] && (
                              <span className="field-error">
                                {formErrors[`${day.key}_end_break_time`]}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Duration Display */}
                        {dayData.start_time && dayData.end_time && (
                          <div
                            style={{
                              marginTop: "var(--space-xs)",
                              padding: "var(--space-sm)",
                              background: "var(--neutral-200)",
                              borderRadius: "var(--border-radius)",
                              fontSize: "0.875rem",
                              textAlign: "center",
                            }}
                          >
                            <strong>Jornada:</strong> {dayData.start_time} -{" "}
                            {dayData.end_time}
                            {dayData.start_break_time &&
                              dayData.end_break_time && (
                                <span>
                                  {" "}
                                  | <strong>Descanso:</strong>{" "}
                                  {dayData.start_break_time} -{" "}
                                  {dayData.end_break_time}
                                </span>
                              )}
                          </div>
                        )}
                      </div>
                    )}

                    {!isActive && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "var(--space-md)",
                          color: "var(--neutral-300)",
                          fontStyle: "italic",
                        }}
                      >
                        Cerrado
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            <hr
              style={{
                borderColor: "var(--neutral-200)",
                margin: "var(--space-md) 0",
              }}
            />

            <div className="action-footer-form-appointment">
              {isEditMode && (
                <button
                  type="button"
                  className="btn--delete-form-appointment"
                  onClick={handleDeleteClick}
                  disabled={saving || deleting}
                >
                  <i className="fas fa-trash-alt"></i>
                  {deleting ? "Eliminando..." : "Eliminar Horario"}
                </button>
              )}
              <button
                type="submit"
                className={`btn btn--primary btn--large btn--save-form-appointment ${
                  !isEditMode ? "btn--full-width-in-create" : ""
                }`}
                disabled={saving || deleting}
              >
                <i className="fas fa-save"></i>{" "}
                {saving
                  ? isEditMode
                    ? "Guardando..."
                    : "Creando..."
                  : isEditMode
                    ? "Guardar Cambios"
                    : "Crear Horario"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ScheduleForm;
